import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AddArticleService } from '@configurator/project/add-article.service';
import { AssistantDataService } from '../assistant-data.service';
import { createDistributionRequirements, createSupplyRequirements, DistributionAlignment, DistributionConnectionCount, DistributionTerminalsAssistantRequirement } from './distribution-terminals-assistant.model';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { ValidationService } from '@project/manager/validation.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { NotEnoughSpaceError } from '@project/components/article/processor/add-article-error';
import { ArticleVO } from '@article/model/vo/article.vo';
import { JumperService } from '@project/accessory/jumper.service';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';

export class NotEnoughRequiredSpaceError extends Error {
  constructor(message: string, public requiredSpace: number) {
    super(message);
  }
}

export class SupplyArticleNotFoundError extends Error {
  constructor(message: string) {
    super(message);
  }
}

export class DistributionArticleNotFoundError extends Error {
  constructor(message: string, public connectionCount: DistributionConnectionCount) {
    super(message);
  }
}

@Injectable({
  providedIn: 'root'
})
export class DistributionTerminalsAssistantService {

  constructor(private assistantDataService: AssistantDataService,
              private undoRedoManager: UndoRedoManager,
              private addArticleService: AddArticleService,
              private articleDataService: ArticleDataService,
              private validationService: ValidationService,
              private jumperService: JumperService) {
  }

  public async addDevices(requirement: DistributionTerminalsAssistantRequirement): Promise<void> {
    const articleNos = await this.createArticleStructure(requirement);
    const fixedArticleNos = await lastValueFrom(this.validationService.quickFix(articleNos));
    await this.undoRedoManager.doWithSequence(async () => {
      const insertedArticleGuids = await this.addArticles(fixedArticleNos);
      await this.jumperService.addJumpersToArticles(insertedArticleGuids, 1);
    });
  }

  private async addArticles(articleNos: string[]): Promise<string[]> {
    const articles = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable(articleNos));
    try {
      return await this.addArticleService.addArticlesToMountingRail(articles);
    } catch (error) {
      if (error instanceof NotEnoughSpaceError) {
        let requiredSpace = await this.getRequiredSpace(articles);
        throw new NotEnoughRequiredSpaceError(error.message, requiredSpace);
      }
      throw error;
    }
  }

  private async createArticleStructure(requirement: DistributionTerminalsAssistantRequirement): Promise<string[]> {
    const supplyArticle = await this.loadSupplyArticle(requirement);
    const distributionArticles = await this.loadDistributionArticles(requirement);
    const outputsForLeftSide =
      this.distributeOutputs(this.getOutputsForLeftSide(requirement.distribution.outputs, requirement.distribution.alignment), !!distributionArticles.get(4));
    const outputsForRightSide =
      this.distributeOutputs(this.getOutputsForRightSide(requirement.distribution.outputs, requirement.distribution.alignment), !!distributionArticles.get(4));

    return this.assembleArticles(
      requirement.supply.inputs,
      supplyArticle,
      outputsForLeftSide,
      outputsForRightSide,
      distributionArticles);
  }

  private getOutputsForLeftSide(outputs: number, layout: DistributionAlignment) {
    if (layout === 'symmetric') {
      if (outputs <= 2) {
        return 2;
      }
      return Math.ceil(outputs / 2);
    }
    if (layout === 'left') {
      return outputs;
    }
    return 0;
  }

  private getOutputsForRightSide(outputs: number, layout: DistributionAlignment) {
    if (layout === 'symmetric') {
      if (outputs <= 2) {
        return 0;
      }
      return Math.floor(outputs / 2);
    }
    if (layout === 'right') {
      return outputs;
    }
    return 0;
  }

  private assembleArticles(inputs: number,
                           supplyArticle: ArticleWithTechnicalPropertiesDTO,
                           distributionOutputsForLeftSide: DistributionConnectionCount[],
                           distributionOutputsForRightSide: DistributionConnectionCount[],
                           distributionArticles: Map<DistributionConnectionCount, ArticleWithTechnicalPropertiesDTO>): string[] {
    const combinedResults = [
      ...distributionOutputsForLeftSide.map(outputs => distributionArticles.get(outputs)).reverse(),
      ...(inputs === 2 ? [supplyArticle, supplyArticle] : [supplyArticle]),
      ...distributionOutputsForRightSide.map(outputs => distributionArticles.get(outputs))
    ];
    return combinedResults.map(searchResult => searchResult.articleNo);
  }

  private distributeOutputs(outputs: number, include4Outputs: boolean): DistributionConnectionCount[] {
    if (outputs <= 0) {
      return [];
    }
    const availableOutputs: DistributionConnectionCount[] =
      include4Outputs ? [4, 3, 2] : [3, 2];
    for (const availableOutput of availableOutputs) {
      const newRemainingOutputs = outputs - availableOutput;
      if (newRemainingOutputs === 0) {
        return [availableOutput];
      } else if (newRemainingOutputs > 0) {
        return [availableOutput].concat(this.distributeOutputs(newRemainingOutputs, include4Outputs));
      }
    }
    return availableOutputs.slice(-1);
  }

  private async loadSupplyArticle(requirement: DistributionTerminalsAssistantRequirement): Promise<ArticleWithTechnicalPropertiesDTO> {
    const requirements = createSupplyRequirements(requirement);
    const supplyArticles = await lastValueFrom(this.assistantDataService.searchArticles('DistributionTerminalBlockAssistant', requirements, ['4']));
    const supplyArticle = supplyArticles.pop();
    if (supplyArticle === undefined) {
      throw new SupplyArticleNotFoundError("No supply article for the given requirements found.");
    }
    return supplyArticle;
  }

  private async loadDistributionArticles(requirement: DistributionTerminalsAssistantRequirement): Promise<Map<DistributionConnectionCount, ArticleWithTechnicalPropertiesDTO>> {
    const distributionArticles = await lastValueFrom(this.assistantDataService.searchArticles('DistributionTerminalBlockAssistant', createDistributionRequirements(requirement), ['4']));

    const resultMap = new Map<DistributionConnectionCount, ArticleWithTechnicalPropertiesDTO>();

    const distributionArticle4 = distributionArticles.find(article => article.properties[ArticleTechnicalInfoPropertyDef.TOTAL_NUMBER_OF_CONNECTION_POINTS]?.value === 4);

    if (distributionArticle4) {
      resultMap.set(4, distributionArticle4);
    }

    const distributionArticle3 = distributionArticles.find(article => article.properties[ArticleTechnicalInfoPropertyDef.TOTAL_NUMBER_OF_CONNECTION_POINTS]?.value === 3);

    if (distributionArticle3 === undefined) {
      throw new DistributionArticleNotFoundError("No distribution article for the given requirements found.", 3);
    }

    resultMap.set(3, distributionArticle3);

    const distributionArticle2 = distributionArticles.find(article => article.properties[ArticleTechnicalInfoPropertyDef.TOTAL_NUMBER_OF_CONNECTION_POINTS]?.value === 2);

    if (distributionArticle2 === undefined) {
      throw new DistributionArticleNotFoundError("No distribution article for the given requirements found.", 2);
    }

    resultMap.set(2, distributionArticle2);

    return resultMap;
  }

  private async getRequiredSpace(articles: ArticleVO[]): Promise<number> {
    const articleTechnicalSearchResults = await lastValueFrom(this.articleDataService.getArticleInfos(
      articles.map(article => article.identity),
      [ArticleTechnicalInfoPropertyDef.WIDTH]));

    // There is only one entry for each articleId in the search result,
    // therefore we use a map to get the width of each article by its id,
    // afterward we use that map and the original articles list to get the width of the whole structure.
    const articleIdToWith = articleTechnicalSearchResults.reduce(
      (map, searchResult) => {
        map.set(searchResult.articleId, searchResult.properties[ArticleTechnicalInfoPropertyDef.WIDTH].value as number);
        return map;
      }, new Map<number, number>());

    return articles.map(article => articleIdToWith.get(article.identity)).reduce((sum, width) => sum + width, 0) / 1000;
  }
}
